import { type ReactElement } from 'react';
import { BriefcaseBusiness } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@/auth/usePermissions';
import { PageHeader } from '@/components/shell/PageHeader';
import { JobsListPage } from '@/features/platform/components/JobsListPage';

/**
 * Per-tenant Admin section — Jobs tab (PTV2-1021 Phase 5).
 *
 * Renders the current tenant's background jobs. The backend routes the
 * `GET /api/v1/jobs` response by user context: this page is only reachable
 * via `openAdmin`, so the caller is always in a tenant context.
 *
 * The Restart action is gated on `openAdmin` (any admin can restart their
 * tenant's jobs). The permission is already enforced at the route level; here
 * we read `canOpenAdmin` so that a viewer-only admin (READ-only `admin:READ`)
 * cannot restart — only operators with MANAGE or UPDATE can.
 *
 * NOTE: Per the current `openAdmin` rule (permissions.ts), READ|MANAGE|UPDATE|
 * WRITE|CREATE all satisfy `openAdmin`. Restart is an action, so we gate it on
 * `manageConfig` (which requires `admin:MANAGE`) as the closest MANAGE-tier
 * gate available for the admin plane. When a dedicated `manageJobs` permission
 * is added in the future, replace `canManageConfig` with that gate.
 */
export function AdminJobsPage(): ReactElement {
  const { t } = useTranslation();
  const { canManageConfig } = usePermissions();

  return (
    <>
      <PageHeader
        icon={<BriefcaseBusiness className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.jobs.heading')}
        subtitle={t('routes.admin.jobs.subheading')}
      />
      <JobsListPage
        caption={t('routes.admin.jobs.caption')}
        captionNote={t('routes.admin.jobs.captionNote')}
        i18nPrefix="routes.admin.jobs"
        canRestart={canManageConfig}
      />
    </>
  );
}
